import { sessionFetch } from '../../utils/sessionFetch';
import { useEffect } from "react";
import { fetchAPI, getServerEndpoint } from "../../utils/utils";
import { useState } from "react";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import 'chartjs-plugin-datalabels';
import { Chart as ChartJS } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(ChartDataLabels);

const string2color = (str = '', transparency = 1, seed = 1) => {
    const safeStr = str || 'default';
    
    let hash = seed;
    safeStr.split('').forEach(char => {
        hash = char.charCodeAt(0) + ((hash << 5) - hash);
    });

    let colour = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        const blend = Math.round(value * transparency + (255 * (1 - transparency)));
        colour += blend.toString(16).padStart(2, '0');
    }
    return colour;
}

function ColorLegend({ tipus }) {
    return (
        <div
            style={{
                borderRadius: 5,
                backgroundColor: string2color(tipus),
                width: 25,
                height: 15,
            }}
        >
            &nbsp;
        </div>
    )
}

function IncomeSeries({ 
    passatFirst, 
    filteredEvents, 
    filtersEvents, 
    setFiltersEvents, 
    startDate,
    availableYears,
    COLLA,
    selectedYear,
    setSelectedYear
}) {
    const [incomeDetails, setIncomeDetails] = useState({})
    const [hiddenEvents, setHiddenEvents] = useState([])
    const [collapsedEvents, setCollapsedEvents] = useState([])
    const [isExporting, setIsExporting] = useState(false)

    useEffect(() => {
        filteredEvents.forEach(ev => {
            if (!ev.id) return;

            fetchAPI(`/get_form_total_money/${ev.id}`, (data) => {
                setIncomeDetails(prev => ({
                    ...prev,
                    [ev.id]: data
                }))
            })
        })
    }, [
        filteredEvents.length
    ])

    const totalMoney = Object.fromEntries(
        Object.entries(incomeDetails).map(([id, d]) => [id, d.total_money])
    )

    const getFormTitle = (ev) => {
        if (ev.form_title) return ev.form_title;
        if (ev.title) {
            try {
                const bytes = Uint8Array.from(window.atob(ev.title), c => c.charCodeAt(0));
                return new TextDecoder().decode(bytes);
            } catch {
                return window.atob(ev.title);
            }
        }
        return 'Sense títol';
    }

    const exportToSheets = async () => {
        setIsExporting(true)
        try {
            const eventIds = eventsWithMoney.map(ev => ev.id)
            const yearLabel = COLLA === 'sarri'
                ? `${selectedYear}`
                : `Setembre ${selectedYear} - Agost ${selectedYear + 1}`
            const response = await sessionFetch(`${getServerEndpoint(COLLA)}/api/export_income_sheets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': '453dabb4-7645-4626-a1bb-477dff3aa557'
                },
                body: JSON.stringify({
                    eventIds,
                    title: `Ingressos ${yearLabel}`,
                })
            })
            const data = await response.json()
            if (!response.ok || !data?.url) {
                throw new Error(data?.error || 'No s\'ha pogut crear el Google Sheet')
            }
            window.open(data.url, '_blank')
        } catch (error) {
            alert(error.message)
        } finally {
            setIsExporting(false)
        }
    }

    const eventsWithMoney = filteredEvents
        .filter(ev => totalMoney?.[ev.id] > 0)
        .filter(ev => !hiddenEvents.includes(ev.id))
        .sort((a, b) => moment(a['data-esperada-inici']).diff(moment(b['data-esperada-inici'])));

    const counts = eventsWithMoney
        .map(ev => Math.round(totalMoney?.[ev.id] || 0))

    const labels = eventsWithMoney
        .map(ev => getFormTitle(ev))

    const colors = eventsWithMoney
        .map(ev => [
            moment() < moment(ev['data-esperada-fi']), 
            ev.tipus || 'default'
        ])
        .map(([past, tipus]) => 
            string2color(tipus, ((passatFirst && past) || (!passatFirst && !past)) ? 0.5 : 1)
        );

    const totalMoneyRaised = Math.round(counts.reduce((acc, count) => acc + count, 0));

    const minCount = Math.max(0, Math.min(...counts) - 2);
    const maxCount = Math.max(...counts) + 2;

    const data = {
        labels: labels,
        datasets: [{
            label: "Ingressos (€)",
            data: counts,
            backgroundColor: colors,
            datalabels: {
                anchor: 'end',
                align: 'top',
            }
        }],
    };

    const lengthYears = moment().diff(moment('01/01/2022', 'DD/MM/YYYY'), 'years')

    const options = {
        plugins: {
            legend: {
                display: false,
            },
            datalabels: {
                color: '#666',
                font: {
                    weight: 'bold',
                    size: 10
                },
                formatter: (value) => {
                    const roundedValue = Math.round(value);
                    return `${roundedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}€`;
                },
                offset: 5
            }
        },
        scales: {
            y: {
                min: 0,
                max: Math.ceil((maxCount + 100) / 500) * 500,
            },
            x: {
                ticks: {
                    callback: function(index) {
                        const label = labels?.[index] ?? '';
                        // Limit label length and add ellipsis if too long
                        return label.length > 10 ? label.substring(0, 10) + '...' : label;
                    },
                    maxRotation: 45,
                    minRotation: 45
                },
            },
        },
        responsive: true,
        animation: {
            duration: 0,
        },
        maintainAspectRatio: false,
        onClick: function(event, elements, chart) {
            if (elements.length > 0) {
                const firstElement = elements[0];
                const index = firstElement.index;
                const clickedEvent = eventsWithMoney[index];
                setHiddenEvents(prev => [...prev, clickedEvent.id]);
            }
        }
    };
    
    return (<>
        <div style={{ marginBottom: 20 }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 12,
                height: '30px' // Fixed height to ensure vertical centering
            }}>
                <h3 style={{ 
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%'
                }}>Ingressos per activitat</h3>
                <div style={{ 
                    color: '#666',
                    fontSize: '16px',
                    display: 'flex', 
                    alignItems: 'center',
                    height: '100%'
                }}>
                    Total: <strong style={{ marginLeft: 5 }}>{totalMoneyRaised.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}€</strong>
                </div>
            </div>
            
            <select 
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{ 
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: '#f8f8f8',
                    fontSize: '14px',
                    color: '#444',
                    cursor: 'pointer',
                    outline: 'none',
                    marginBottom: 15
                }}
            >
                {availableYears.map(year => (
                    <option key={year} value={year}>
                        {COLLA === 'sarri' 
                            ? `${year}` 
                            : `Setembre ${year} - Agost ${year + 1}`}
                    </option>
                ))}
            </select>
        </div>
        {
          eventsWithMoney.length === 0 ? (
            <div style={{ fontSize: '0.8rem', fontWeight: '300', color: '#555', marginBottom: 50 }}>
              No hi ha formularis en aquest rang de dates.
            </div>
          ) : (
            <div style={{ width: '100%', height: window.innerWidth > 768 ? 300 : 200 }}>
                <Bar data={data} options={options} />
            </div>
          )
        }
        {hiddenEvents.length > 0 && (
            <div style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {hiddenEvents.map(eventId => {
                        const event = filteredEvents.find(e => e.id === eventId);
                        return (
                            <button
                                key={eventId}
                                onClick={() => setHiddenEvents(prev => prev.filter(id => id !== eventId))}
                                style={{
                                    padding: '5px 10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    backgroundColor: '#f8f8f8',
                                    cursor: 'pointer',
                                    color: '#666',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 5
                                }}
                            >
                                <strong>{Math.round(totalMoney?.[eventId] || 0).toLocaleString('de-DE')}€</strong>
                                {getFormTitle(event)} ✕
                            </button>
                        );
                    })}
                </div>
            </div>
        )}

        {eventsWithMoney.length > 0 && (
            <div style={{ marginTop: 30 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0 }}>Detall per formulari</h3>
                    <button
                        onClick={exportToSheets}
                        disabled={isExporting}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: isExporting ? '#adb5bd' : '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: 6,
                            cursor: isExporting ? 'not-allowed' : 'pointer',
                            fontSize: 13,
                        }}
                    >
                        {isExporting ? '⏳ Creant...' : '📊 Exportar a Sheets'}
                    </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {[...eventsWithMoney]
                        .sort((a, b) => (totalMoney?.[b.id] || 0) - (totalMoney?.[a.id] || 0))
                        .map((ev, index) => {
                            const details = incomeDetails[ev.id];
                            const isExpanded = !collapsedEvents.includes(ev.id);
                            const money = Math.round(totalMoney?.[ev.id] || 0);
                            const responses = details?.num_responses || 0;
                            const tickets = details?.tickets || [];

                            return (
                                <div key={ev.id}>
                                    <div
                                        onClick={() => setCollapsedEvents(prev =>
                                            isExpanded ? [...prev, ev.id] : prev.filter(id => id !== ev.id)
                                        )}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            padding: '10px 12px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                            backgroundColor: isExpanded ? '#f8f8f8' : 'transparent',
                                        }}
                                    >
                                        <span style={{ color: '#999', fontSize: 13, width: 20, textAlign: 'right', flexShrink: 0 }}>
                                            {index + 1}.
                                        </span>
                                        <ColorLegend tipus={ev.tipus || 'default'} />
                                        <span style={{ flex: 1, fontSize: 14 }}>
                                            {getFormTitle(ev)}
                                        </span>
                                        <span style={{ color: '#888', fontSize: 13, flexShrink: 0 }}>
                                            {responses} resp.
                                        </span>
                                        <strong style={{ fontSize: 14, flexShrink: 0, minWidth: 60, textAlign: 'right' }}>
                                            {money.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}€
                                        </strong>
                                        <span style={{ color: '#aaa', fontSize: 12, flexShrink: 0 }}>
                                            {isExpanded ? '▲' : '▼'}
                                        </span>
                                    </div>

                                    {isExpanded && tickets.length > 0 && (
                                        <div style={{ padding: '6px 12px 6px 54px', borderBottom: '1px solid #eee' }}>
                                            {tickets.map((ticket, i) => (
                                                <div
                                                    key={i}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '3px 0',
                                                        fontSize: 13,
                                                        color: '#777',
                                                    }}
                                                >
                                                    <span>
                                                        <span style={{ fontWeight: 500, color: '#555' }}>{ticket.quantity} ×</span> {ticket.name}
                                                    </span>
                                                    <span style={{ flexShrink: 0, marginLeft: 12 }}>
                                                        {Number(ticket.price).toLocaleString('de-DE')}€ = <strong style={{ color: '#555' }}>{Math.round(ticket.total).toLocaleString('de-DE')}€</strong>
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    }
                </div>
            </div>
        )}
    </>);
}

export default IncomeSeries;
